import {
	type Dispatch,
	type FC,
	type SetStateAction,
	useMemo,
	useState
} from 'react'
import { useOutletContext } from 'react-router'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalSidebar } from '@/components/commons/gallery-modal-sidebar'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { AllocateDropdown } from '@/features/photoset/components/allocate-dropdown'
import { GalleryAllocationSidebar } from '@/features/photoset/components/gallery-photoset-modal/gallery-allocation-modal/gallery-allocation-sidebar'
import { useGalleryAllocationShortcuts } from '@/features/photoset/hooks/use-gallery-allocation-shortcuts'
import { useDeallocateImages } from '@/features/photoset/queries/use-deallocate-images'
import { useReallocateImages } from '@/features/photoset/queries/use-reallocate-images'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type {
	Image,
	OnAllocateAction,
	PhotosetURLParams
} from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface GalleryAllocationModal {
	selectableImages: Omit<Image, 'space'>[]
	currentDetailImageIndex: number
	setCurrentDetailImageIndex: Dispatch<SetStateAction<number | null>>
	onClose: () => void
}

export const GalleryAllocationModal: FC<GalleryAllocationModal> = ({
	selectableImages,
	currentDetailImageIndex,
	setCurrentDetailImageIndex,
	onClose
}) => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const selectedImages = usePhotosetStore((store) => store.selectedImages)
	const selectImages = usePhotosetStore((store) => store.selectImages)
	const unselectImages = usePhotosetStore((store) => store.unselectImages)
	const allocateImages = usePhotosetStore((state) => state.allocateImages)
	const deallocateImages = usePhotosetStore((store) => store.deallocateImages)
	const undoLatestAction = usePhotosetStore((store) => store.undoLatestAction)
	const allocationHistory = usePhotosetStore(
		(store) => store.allocationHistory
	)
	const unselectAll = usePhotosetStore((store) => store.unselectAll)
	const [modalRef, setModalRef] = useState<HTMLDivElement | null>(null)
	const deallocateImagesMutation = useDeallocateImages()
	const allocateImagesMutation = useReallocateImages()

	// Handle allocate action
	const onAllocate = async ({
		photosetSpaceId,
		imagesIds = selectedImages,
		isShortcut = false,
		keepSelected = false,
		trackInHistory = false
	}: OnAllocateAction) => {
		try {
			await allocateImagesMutation.mutateAsync({
				urlParams: {
					photosetId: urlParams.id,
					photosetSpaceId
				},
				payload: imagesIds
			})

			if (isShortcut && !trackInHistory) {
				undoLatestAction(keepSelected)
			} else {
				allocateImages({
					imagesIds,
					photosetSpaceId,
					keepSelected,
					trackInHistory
				})
			}

			const nextImage =
				selectableImages[currentDetailImageIndex + 1] ?? null
			unselectAll()

			if (nextImage) {
				selectImages([nextImage.id])
			} else {
				onClose()
			}
		} catch (error) {
			errorHandler(error)
		}
	}

	// Handle allocate action
	const onDeallocate = async ({
		photosetSpaceId,
		imagesIds,
		isShortcut = false,
		keepSelected = false,
		trackInHistory = false
	}: OnAllocateAction) => {
		try {
			await deallocateImagesMutation.mutateAsync({
				urlParams: {
					photosetId: urlParams.id,
					photosetSpaceId
				},
				payload: imagesIds
			})

			if (isShortcut) {
				undoLatestAction(keepSelected)
				return
			}

			deallocateImages({
				imagesIds,
				photosetSpaceId,
				keepSelected,
				trackInHistory
			})
		} catch (error) {
			errorHandler(error)
		}
	}

	// Handle allocate or deallocate action
	const onUndo = ({
		imagesIds,
		photosetSpaceId,
		isShortcut
	}: OnAllocateAction) => {
		// Get the latest allocate action from the history timeline
		const imageAllocationHistory = allocationHistory.find(
			(history) =>
				history.imagesIds.includes(imagesIds[0]) &&
				photosetSpaceId === history.photosetSpaceId
		)

		if (imageAllocationHistory?.type === 'allocation') {
			onDeallocate({
				photosetSpaceId,
				imagesIds,
				isShortcut,
				keepSelected: true,
				trackInHistory: false
			})
		}

		if (imageAllocationHistory?.type === 'deallocation') {
			onAllocate({
				photosetSpaceId,
				imagesIds,
				isShortcut,
				keepSelected: false,
				trackInHistory: false
			})
		}
	}

	useGalleryAllocationShortcuts({
		currentDetailImageIndex,
		selectableImages,
		onAllocate,
		onUndo,
		onClose,
		setCurrentDetailImageIndex,
		isPending:
			allocateImagesMutation.isPending ||
			deallocateImagesMutation.isPending
	})

	const currentImage = useMemo(
		() =>
			selectableImages[currentDetailImageIndex] ||
			selectableImages[currentDetailImageIndex - 1],
		[currentDetailImageIndex, selectableImages]
	)

	return (
		<GalleryModal
			className='bg-neutral-white/80 backdrop-blur-md'
			currentImage={currentImage}
			headerSlot={
				<GalleryModalHeader
					currentImage={currentImage}
					endSlot={
						<AllocateDropdown
							container={modalRef}
							onAllocate={onAllocate}
						/>
					}
					imagesCount={selectableImages.length}
					onClose={onClose}
				/>
			}
			onClose={onClose}
			ref={setModalRef}
			sidebarSlot={
				<GalleryModalSidebar
					content={
						<GalleryAllocationSidebar onAllocate={onAllocate} />
					}
					title={t('photoset:allocation.sidebar_title')}
				/>
			}
			thumbnails={currentImage.thumbnails}
			thumbnailsSlot={
				<GalleryModalThumbnails
					currentImageId={currentImage.id}
					images={selectableImages}
					onSelect={(image, index) => {
						unselectImages([
							selectableImages[currentDetailImageIndex].id
						])
						selectImages([image.id])
						setCurrentDetailImageIndex(index)
					}}
					thumbnails={'thumbnails'}
				/>
			}
		/>
	)
}
